// Ghost replays. A run is a pure function of the environment, the vehicle stats and the per-tick
// throttle, so a ghost is stored as just those inputs and replayed in its own headless Sim.
import type { EnvironmentDef } from '../config/environments';
import { SIM } from '../config/tuning';
import { vehicleById, type VehicleId } from '../config/vehicles';
import { createSim, disposeSim, type Sim } from './createSim';
import { decodeInputs, encodeInputs, type GhostRecord } from './ghostFormat';
import type { Run } from './run';
import { vehicleStats, type UpgradeLevels } from './upgrades';

// The record format and its pure helpers live in ghostFormat.ts (the leaderboard server shares them).
export { countInputs, decodeInputs, encodeInputs, MAX_GHOST_TICKS, sanitizeGhost, type GhostRecord } from './ghostFormat';

/** Captures a finished run as a ghost record. */
export function recordGhost(run: Run, envId: string, vehicle: VehicleId, levels: UpgradeLevels): GhostRecord {
  return {
    version: SIM.version,
    envId,
    vehicle,
    levels: { ...levels },
    idle: run.idleTicks,
    inputs: encodeInputs(run.inputs),
    distance: run.state.distance,
  };
}

/** A ghost being replayed: its own Sim, stepped in lockstep with the live run's running ticks. */
export class GhostReplay {
  private readonly inputs: number[];
  private cursor = 0;

  private constructor(
    readonly record: GhostRecord,
    readonly sim: Sim,
  ) {
    this.inputs = decodeInputs(record.inputs);
  }

  /** Creates the replay Sim and waits on the start line exactly as long as the recording did. */
  static async create(record: GhostRecord, env: EnvironmentDef): Promise<GhostReplay> {
    const vehicle = vehicleById(record.vehicle)!;
    const sim = await createSim(env, vehicleStats(record.levels, vehicle));
    sim.engine.step(record.idle);
    sim.run.drainEvents();
    return new GhostReplay(record, sim);
  }

  get finished(): boolean {
    return this.cursor >= this.inputs.length;
  }

  /** Advances to `runningTicks` (the live run's running ticks); returns how many ticks were stepped. */
  advanceTo(runningTicks: number): number {
    let stepped = 0;
    while (this.cursor < runningTicks && this.cursor < this.inputs.length) {
      this.sim.run.throttle = this.inputs[this.cursor++] / 100;
      this.sim.engine.step(1);
      stepped += 1;
    }
    if (stepped) this.sim.run.drainEvents(); // presentation never reads ghost events
    return stepped;
  }

  /** After the last input: true when the replay reproduced the recorded run (determinism self-check). */
  matchesRecording(): boolean {
    return Math.abs(this.sim.run.state.distance - this.record.distance) < 0.01;
  }

  dispose(): void {
    disposeSim(this.sim);
  }
}
